import type { ReactNode } from 'react'

import { useState } from 'react'
import { ChevronDownIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import { cn } from '@/shared/lib/utils'

export type InterpretationCardProps = {
	icon: ReactNode
	title: string
	badge: ReactNode
	headerExtra?: ReactNode
	mini?: ReactNode
	blocking?: boolean
	blockingTestId?: string
	toggleTestId: string
	'data-testid': string
	children: ReactNode
}

export const InterpretationCard = ({
	icon,
	title,
	badge,
	headerExtra,
	mini,
	blocking = false,
	blockingTestId,
	toggleTestId,
	'data-testid': testId,
	children,
}: InterpretationCardProps) => {
	const { t } = useTranslation()
	const [open, setOpen] = useState(true)

	return (
		<Collapsible
			open={open}
			onOpenChange={setOpen}
			className={cn(
				'rounded-xl border bg-card',
				blocking && 'border-l-2 border-l-destructive'
			)}
			data-testid={testId}
		>
			<div className='flex items-center gap-2.5 px-3.5 py-3'>
				{icon}
				<span className='text-sm font-semibold'>{title}</span>
				{badge}
				{blocking && (
					<span
						className='text-xs font-bold tracking-wide text-destructive uppercase'
						data-testid={blockingTestId}
					>
						{t('midterm_forecast.blocks_forecast', {
							defaultValue: 'Blocks forecast',
						})}
					</span>
				)}
				{headerExtra}
				{mini ? (
					<div className='ml-auto w-24 shrink-0'>{mini}</div>
				) : (
					<div className='ml-auto' />
				)}
				<CollapsibleTrigger asChild>
					<Button
						variant='ghost'
						size='icon-sm'
						data-testid={toggleTestId}
						className='data-[state=open]:rotate-180'
						aria-label={t('midterm_forecast.toggle_details', {
							defaultValue: 'Toggle details',
						})}
					>
						<ChevronDownIcon className='size-4' />
					</Button>
				</CollapsibleTrigger>
			</div>
			<CollapsibleContent>
				<div className='flex flex-col gap-3 border-t px-3.5 pt-3 pb-3.5'>
					{children}
				</div>
			</CollapsibleContent>
		</Collapsible>
	)
}
